import { removeFalsy } from "helpers/formatter";
import { bufferFitlinxxId, logMessage } from "helpers/utils";
import {
  NonTCAMembersListEntry,
  TCAMembersListEntry,
  TMember,
  TMemberDetails,
  TMemberSummaryCardioAgainstGoal,
  TMemberSummaryStrengthAgainstGoal,
  TMemberTotalWorkoutStatistics,
} from "models/member";
import {
  TCardioBreakdownByExerciseReport,
  TCardioBreakdownByExerciseTypeReport,
  TCardioBreakdownForLast7DaysReport,
  TCardioCumulativeCalories,
  TCardioDetailByDate,
  TCardioExerciseDetail,
  TCardioExerciseSummary,
  TCardioSummary,
  TMemberWorkingOut,
  TStrengthBreakdownByExercise,
  TStrengthBreakdownByMuscleGroup,
  TStrengthBreakdownPerRep,
  TStrengthTotalWeightLifted,
} from "models/report";
import { TPGPLongTermGoalCollection } from "models/tca";
import apiService from "modules/api/apiService";
import {
  GET_MEMBERS,
  MEMBER_CARDIO_BREAKDOWN_BY_EXERCISE_REPORT,
  MEMBER_CARDIO_BREAKDOWN_BY_EXERCISE_TYPE_REPORT,
  MEMBER_CARDIO_BREAKDOWN_FOR_LAST_7_DAYS_REPORT,
  MEMBER_CARDIO_CUMULATIVE_CALORIES_REPORT,
  MEMBER_CARDIO_DETAIL_BY_DATE_REPORT,
  MEMBER_CARDIO_EXERCISE_DETAIL_REPORT,
  MEMBER_CARDIO_EXERCISE_SUMMARY_REPORT,
  MEMBER_CARDIO_SUMMARY_REPORT,
  MEMBER_CRUD,
  MEMBER_DATA,
  MEMBER_JOURNAL_ENTRY,
  MEMBER_JOURNAL_ENTRY_EDIT,
  MEMBER_JOURNAL_LIST,
  MEMBER_MASS_DELETE,
  MEMBER_MASS_INSTRUCTOR_REASSIGN,
  MEMBER_MASS_INSTRUCTOR_SWITCH,
  MEMBER_MASS_STATUS_REASSIGN,
  MEMBER_STATISTICS,
  MEMBER_STRENGTH_AVERAGE_WEIGHT_PER_REP,
  MEMBER_STRENGTH_BREAKDOWN_BY_EXERCISE,
  MEMBER_STRENGTH_BREAKDOWN_BY_MUSCLE_GROUP,
  MEMBER_STRENGTH_DETAIL_BY_DATE,
  MEMBER_STRENGTH_DETAIL_BY_EXERCISE,
  MEMBER_STRENGTH_EXERCISE_SUMMARY,
  MEMBER_STRENGTH_PROGRAM_SUMMARY,
  MEMBER_STRENGTH_TOTAL_WEIGHT_LIFTED,
  MEMBER_STRENGTH_TOTAL_WEIGHT_LIFTED_AGAINST_GOALS_PER_SESSION,
  MEMBER_STRENGTH_WORKOUT_COMPARISON,
  MEMBER_SUMMARY_CARDIO_AGAINST_GOAL,
  MEMBER_SUMMARY_STRENGTH_AGAINST_GOAL,
  MEMBER_UPDATE_STATUS,
  MEMBER_WORKING_OUT_LIST,
  NON_TCA_MEMBERS,
  TCA_EMAIL_CONTRACT,
  TCA_LONG_TERM_GOALS_CRUD,
  TCA_MEMBERS,
  TCA_PFI,
  TCA_REMS_SCORING,
  TCA_RESET_APPOINTMENTS,
} from "modules/api/endpoint";
import { TPagination } from "modules/api/type";
import { toast } from "react-toastify";

export const getMember = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMember;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(`${MEMBER_CRUD}${queryParams ? `?${queryParams}` : ""}`);

    //console.log(`results from getMember ${JSON.stringify(results)}`);
    // #region buffering fitlinxx ID
    let resultToReturn: TMember = results.data.payload[0];

    resultToReturn.fitlinxx_id = bufferFitlinxxId(resultToReturn.fitlinxx_id);
    // #endregion buffering fitlinxx ID

    return {
      data: resultToReturn,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const createMember = async (
  body: any,
  tcaData: any,
  setLoadingState: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    //console.log(`Body.home_facility is [${body.home_facility}]`);
    //console.log(`Body(create): ${JSON.stringify(body)}`);
    let result = await apiService.post(MEMBER_CRUD, body);
    //console.log(`After createMember API call. ${JSON.stringify(result)}`);
    return {
      data: result.data.payload,
      success: true,
    };
  } catch (error) {
    console.log(`createMember got an error. ${JSON.stringify(error.response.data.errors)}`);
    toast(
      `Problem saving member. ${JSON.stringify(
        error.response.data.errors
      )} Try again or contact support for TCA data.`,
      {
        type: "error",
      }
    );
    logMessage({
      member_id: body.member_id,
      message: `Caught frontend exception. [${error}], [${JSON.stringify(
        error.response.data.errors
      )}]\nMember: [${JSON.stringify(body)}]\nTCA:[${JSON.stringify(tcaData) ?? "No TCA Data"}]`,
    });
    if (setLoadingState !== null) {
      setLoadingState(false);
    }
    return {
      success: false,
    };
  }
};

export const updateMember = async (
  body: any,
  setLoadingState: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    //console.log(`Body(update):\n`, JSON.stringify(body));
    let result = await apiService.put(MEMBER_CRUD, body);
    //console.log(`Result(update):\n`, result);
    return {
      data: result.data.payload,
      success: true,
    };
  } catch (error) {
    console.log(`updateMember got an error. ${JSON.stringify(error.response.data.errors)}`);
    toast(`Problem updating member. ${JSON.stringify(error.response.data.errors)}.`, {
      type: "error",
    });
    logMessage({
      member_id: body.member_id,
      message: `Caught frontend exception. [${error}], [${JSON.stringify(
        error.response.data.errors
      )}]`,
    });
    if (setLoadingState !== null) {
      setLoadingState(false);
    }
    return {
      success: false,
    };
  }
};

export const updateMemberStatus = async (
  params
): Promise<{
  success: boolean;
  message?: string;
  errors?: any[];
}> => {
  try {
    const results = await apiService.put(MEMBER_UPDATE_STATUS, params);
    return {
      success: results.data.success,
    };
  } catch (error) {
    return {
      errors: error.response.data.errors,
      message: error.response.data.message,
      success: false,
    };
  }
};

export const getMemberList = async (
  params = {}
): Promise<{
  success: boolean;
  data?: any[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(`${GET_MEMBERS}${queryParams ? `?${queryParams}` : ""}`);
    //console.log("results.data.payload.data has: \n", results.data.payload.data);
    //Data needed transformations
    const entries = results.data.payload.data.map((e) => {
      //Formatting date
      e.last_workout_session = e.last_workout_session?.replace(` `, `T`);
      // #region buffering fitlinxx ID
      e.fitlinxx_id = bufferFitlinxxId(e.fitlinxx_id);
      // #endregion buffering fitlinxx ID

      return e;
    });

    return {
      data: entries,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const getMemberById = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMemberDetails;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(`${MEMBER_DATA}${queryParams ? `?${queryParams}` : ""}`);

    // #region buffering fitlinxx ID
    let resultToReturn: TMemberDetails = results.data.payload[0];
    resultToReturn.membership.fitlinxx_id = bufferFitlinxxId(resultToReturn.membership.fitlinxx_id);
    // #endregion buffering fitlinxx ID

    return {
      data: resultToReturn,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const getMemberTotalWorkoutStatistics = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMemberTotalWorkoutStatistics;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STATISTICS}${queryParams ? `?${queryParams}` : ""}`
    );
    //console.log("memberServices.ts: getMemberTotalWorkoutStatistics: queryParams:\n", queryParams);
    //console.log("memberServices.ts: getMemberTotalWorkoutStatistics: Results:\n", results);

    return {
      data: results.data.payload[0],
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const getCardioBreakdownByExerciseList = async (
  params: any
): Promise<{
  success: boolean;
  data?: TCardioBreakdownByExerciseReport[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_CARDIO_BREAKDOWN_BY_EXERCISE_REPORT}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data.payload.data, success: true }; // Todo handle pagination if requires
  } catch (error) {
    return { success: false };
  }
};

export const getCardioBreakdownByExerciseTypeData = async (
  params: any
): Promise<{
  success: boolean;
  data?: TCardioBreakdownByExerciseTypeReport[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_CARDIO_BREAKDOWN_BY_EXERCISE_TYPE_REPORT}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getCardioBreakdownForLast7DaysList = async (
  params: any
): Promise<{
  success: boolean;
  data?: TCardioBreakdownForLast7DaysReport[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_CARDIO_BREAKDOWN_FOR_LAST_7_DAYS_REPORT}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getCardioCumulativeCalories = async (
  params: any
): Promise<{
  success: boolean;
  data?: TCardioCumulativeCalories[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_CARDIO_CUMULATIVE_CALORIES_REPORT}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getCardioDetailByDateList = async (params: {
  view_interval_options: string;
  site: string;
  member_id: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TCardioDetailByDate[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${MEMBER_CARDIO_DETAIL_BY_DATE_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getCardioExerciseDetailList = async (params: {
  view_interval_options: string;
  site: string;
  member_id: string;
  exercise_id: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TCardioExerciseDetail[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${MEMBER_CARDIO_EXERCISE_DETAIL_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getCardioExerciseSummaryList = async (params: {
  view_interval_options: string;
  site: string;
  member_id: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TCardioExerciseSummary[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${MEMBER_CARDIO_EXERCISE_SUMMARY_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getCardioSummaryList = async (params: {
  view_interval_options: string;
  site: string;
  member_id: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TCardioSummary[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${MEMBER_CARDIO_SUMMARY_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberWorkingoutList = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMemberWorkingOut[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_WORKING_OUT_LIST}${queryParams ? `?${queryParams}` : ""}`
    );

    //Data needed transformations
    const entries = results.data.payload.data.map((e) => {
      // #region buffering fitlinxx ID
      e.fitlinxx_id = bufferFitlinxxId(e.fitlinxx_id);
      // #endregion buffering fitlinxx ID

      return e;
    });

    return {
      data: entries,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const getMemberJournals = async (
  params: any
): Promise<{
  success: boolean;
  data?: any[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_JOURNAL_LIST}${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const addJournalEntry = async (
  body: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.post(MEMBER_JOURNAL_ENTRY, body);
    return {
      data: result,
      success: true,
    };
  } catch (e) {
    return {
      message: e.message,
      success: false,
    };
  }
};

export const editJournalEntry = async (
  memberId,
  journalId,
  body: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.put(MEMBER_JOURNAL_ENTRY_EDIT(memberId, journalId), body);
    return {
      data: result,
      success: true,
    };
  } catch (e) {
    return {
      message: e.message,
      success: false,
    };
  }
};

export const getStrengthAverageWeightPerRep = async (
  params: any
): Promise<{
  success: boolean;
  data?: TStrengthBreakdownPerRep[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_AVERAGE_WEIGHT_PER_REP}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthBreakdownByExercise = async (
  params: any
): Promise<{
  success: boolean;
  data?: TStrengthBreakdownByExercise;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_BREAKDOWN_BY_EXERCISE}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthBreakdownByExerciseAsPDF = async (params: any): Promise<any> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_BREAKDOWN_BY_EXERCISE}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthBreakdownByMuscleGroup = async (
  params: any
): Promise<{
  success: boolean;
  data?: TStrengthBreakdownByMuscleGroup;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_BREAKDOWN_BY_MUSCLE_GROUP}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthBreakdownByMuscleGroupAsPDF = async (params: any): Promise<any> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_BREAKDOWN_BY_MUSCLE_GROUP}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data, success: true };
  } catch (error) {
    return { success: false };
  }
};

//TODO: confirm the data type
export const getStrengthDetailByDate = async (
  params: any
): Promise<{
  success: boolean;
  data?: any;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_DETAIL_BY_DATE}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

//TODO: confirm the data type
export const getStrengthDetailByExercise = async (
  params: any
): Promise<{
  success: boolean;
  data?: any;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_DETAIL_BY_EXERCISE}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

//TODO: confirm the data type
export const getStrengthExerciseSummary = async (
  params: any
): Promise<{
  success: boolean;
  data?: any;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_EXERCISE_SUMMARY}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

//TODO: confirm the data type
export const getStrengthProgramSummary = async (
  params: any
): Promise<{
  success: boolean;
  data?: any;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_PROGRAM_SUMMARY}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthTotalWeightLifted = async (
  params: any
): Promise<{
  success: boolean;
  data?: TStrengthTotalWeightLifted[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_TOTAL_WEIGHT_LIFTED}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthTotalWeightLiftedAsPDF = async (params: any): Promise<any> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_TOTAL_WEIGHT_LIFTED}${queryParams ? `?${queryParams}` : ""}`
    );

    return { data: results.data, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getStrengthTotalWeightLiftedAgainstGoalsPerSession = async (
  params: any
): Promise<{
  success: boolean;
  data?: TStrengthTotalWeightLifted[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_TOTAL_WEIGHT_LIFTED_AGAINST_GOALS_PER_SESSION}${
        queryParams ? `?${queryParams}` : ""
      }`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

//TODO: confirm the data type
export const getStrengthWorkoutComparison = async (
  params: any
): Promise<{
  success: boolean;
  data?: any;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_STRENGTH_WORKOUT_COMPARISON}${queryParams ? `?${queryParams}` : ""}`
    );
    return { data: results.data.payload, success: true };
  } catch (error) {
    return { success: false };
  }
};

export const addMemberTCARemsScores = async (
  body: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    console.log(`Body(Rems):\n`, body);
    let result = await apiService.post(TCA_REMS_SCORING, body);
    return {
      data: result,
      success: true,
    };
  } catch (error) {
    console.log(`addMemberTCAGoals got an error. ${JSON.stringify(error)}`);
    toast("Problem saving appointment (REMS). Try again or contact support.", {
      type: "error",
    });
    if (body.setLoadingState !== null) {
      body.setLoadingState(false);
    }
    return {
      success: false,
    };
  }
};

export const getMemberTCARemsScores = async (
  params = {}
): Promise<{
  success: boolean;
  data?: {
    remsScore: number;
    remsSubscaleSS: number;
    remsSubscaleSM: number;
    remsSubscaleATD: number;
  };
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${TCA_REMS_SCORING}${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      data: results.data.payload,
      success: true,
    };
  } catch (error) {
    throw error;
  }
};

export const addMemberTCAGoals = async (
  body: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.post(TCA_LONG_TERM_GOALS_CRUD, body);
    console.log(`addMemberTCAGoals: result is ${JSON.stringify(result)}`);
    return {
      data: result,
      success: true,
    };
  } catch (error) {
    console.log(`addMemberTCAGoals got an error.`);
    toast("Problem saving appointment (PGP). Try again or contact support.", {
      type: "error",
    });
    if (body.setLoadingState !== null) {
      body.setLoadingState(false);
    }
    return {
      success: false,
    };
  }
};

export const getMemberTCAGoals = async (
  params = {}
): Promise<{
  success: boolean;
  data?: TPGPLongTermGoalCollection;
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${TCA_LONG_TERM_GOALS_CRUD}${queryParams ? `?${queryParams}` : ""}`
    );
    //Strip the useless properties
    const returnedEntities = results.data.payload.data;
    let entities: TPGPLongTermGoalCollection = {};

    Object.keys(returnedEntities)
      .sort()
      .forEach((k) => {
        entities[k] = returnedEntities[k].map((m) => {
          return m;
        });
      });

    return {
      data: entities,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    throw error;
  }
};

export const addMemberTCAPersonalFeelingsInventory = async (
  body: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    //console.log("addMemberTCAPersonalFeelingsInventory->body:\n", body);
    logMessage({
      member_id: 0,
      message: `energy_score: ${body.energy_score}, fatigue_score: ${body.fatigue_score}, stress_score: ${body.stress_score}`,
    });
    let result = await apiService.post(TCA_PFI, body);
    return {
      data: result,
      success: true,
    };
  } catch (error) {
    console.log(`addMemberTCAPersonalFeelingsInventory got an error.`);
    toast("Problem saving appointment (PFI). Try again or contact support.", {
      type: "error",
    });
    if (body.setLoadingState !== null) {
      body.setLoadingState(false);
    }
    return {
      success: false,
    };
  }
};

export const getMemberTCAPersonalFeelingsInventory = async (
  params = {}
): Promise<{
  success: boolean;
  data?: {
    fatigue_score: number;
    energy_score: number;
    stress_score: number;
    datetime: string;
  }[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(`${TCA_PFI}${queryParams ? `?${queryParams}` : ""}`);

    return {
      data: results.data.payload,
      success: true,
    };
  } catch (error) {
    throw error;
  }
};

export const getTCAMembers = async (
  params = {}
): Promise<{
  success: boolean;
  data?: TCAMembersListEntry[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(`${TCA_MEMBERS}${queryParams ? `?${queryParams}` : ""}`);

    //Data needed transformations
    const entries = results.data.payload.data.map((e) => {
      // #region buffering fitlinxx ID
      e.fitlinxx_id = bufferFitlinxxId(e.fitlinxx_id);
      // #endregion buffering fitlinxx ID

      return e;
    });

    return {
      data: entries,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const getNonTCAMembers = async (
  params = {}
): Promise<{
  success: boolean;
  data?: NonTCAMembersListEntry[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${NON_TCA_MEMBERS}${queryParams ? `?${queryParams}` : ""}`
    );

    //Data needed transformations
    const entries = results.data.payload.data.map((e) => {
      // #region buffering fitlinxx ID
      e.fitlinxx_id = bufferFitlinxxId(e.fitlinxx_id);
      // #endregion buffering fitlinxx ID

      return e;
    });

    return {
      data: entries,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const deleteTCAMemberAppointments = async (
  params = {}
): Promise<{
  success: boolean;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";
  console.log(`deletedTCAMemberAppointments() params is ${JSON.stringify(params)}`);
  console.log(`deletedTCAMemberAppointments() queryParams is ${queryParams}`);
  try {
    const results = await apiService.del(
      `${TCA_RESET_APPOINTMENTS}${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      success: true,
    };
  } catch (error) {
    console.log(`addMemberTCAGoals got an error.`);
    toast("Problem saving appointment (PGP). Try again or contact support.", {
      type: "error",
    });
    if (params["setLoadingState"] !== null) {
      params["setLoadingState"](false);
    }
    return { success: false };
  }
};

export const massReassignInstructor = async (body: {
  instructor_id: number;
  members: number[];
}): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.post(MEMBER_MASS_INSTRUCTOR_REASSIGN, body);
    return {
      data: result,
      success: true,
    };
  } catch (error) {
    throw error.response.data.message;
  }
};

export const massInstructorSwitch = async (body: {
  instructor_id: number;
  members: number[];
}): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.put(MEMBER_MASS_INSTRUCTOR_SWITCH, body);
    return {
      data: result,
      success: true,
    };
  } catch (error) {
    throw error.response.data.message;
  }
};

export const massStatusUpdate = async (body: {
  status: string;
  members: number[];
}): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    let result = await apiService.put(MEMBER_MASS_STATUS_REASSIGN, body);
    return {
      data: result.data.payload,
      success: true,
    };
  } catch (error) {
    throw error.response.data.message;
  }
};

export const massDelete = async (params: {
  members: number[];
}): Promise<{
  success: boolean;
}> => {
  const body = {
    ...params,
    _method: "delete",
  };

  try {
    const results = await apiService.post(MEMBER_MASS_DELETE, body);

    return {
      success: true,
    };
  } catch (error) {
    throw error;
  }
};

export const getMemberSummaryCardioAgainstGoal = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMemberSummaryCardioAgainstGoal[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_SUMMARY_CARDIO_AGAINST_GOAL}${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      data: results.data.payload,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};
export const getMemberSummaryStrengthAgainstGoal = async (
  params: any
): Promise<{
  success: boolean;
  data?: TMemberSummaryStrengthAgainstGoal[];
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const results = await apiService.get(
      `${MEMBER_SUMMARY_STRENGTH_AGAINST_GOAL}${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      data: results.data.payload,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const emailContract = async (
  memberId: number,
  blob,
  preview = false,
  newEmail = "",
  subject = ""
) => {
  const formData = new FormData();
  formData.append("member_id", memberId.toString());
  formData.append("preview", preview.toString());
  formData.append("newEmail", newEmail.toString());
  formData.append("subject", subject.toString());
  if (blob) {
    formData.append("contract", blob, "document.pdf");
  }

  try {
    const response = await apiService.post(TCA_EMAIL_CONTRACT, formData);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("An unexpected error occurred");
    }
  }
};

export default {
  addJournalEntry,
  addMemberTCAGoals,
  addMemberTCAPersonalFeelingsInventory,
  addMemberTCARemsScores,
  createMember,
  deleteTCAMemberAppointments,
  emailContract,
  getCardioBreakdownByExerciseList,
  getCardioBreakdownByExerciseTypeData,
  getCardioBreakdownForLast7DaysList,
  getCardioCumulativeCalories,
  getCardioDetailByDateList,
  getCardioExerciseDetailList,
  getCardioExerciseSummaryList,
  getCardioSummaryList,
  getMemberTCAGoals,
  getMemberTCAPersonalFeelingsInventory,
  getMemberTCARemsScores,
  getMemberWorkingoutList,
  getNonTCAMembers,
  getStrengthAverageWeightPerRep,
  getStrengthBreakdownByExercise,
  getStrengthBreakdownByExerciseAsPDF,
  getStrengthBreakdownByMuscleGroup,
  getStrengthBreakdownByMuscleGroupAsPDF,
  getStrengthDetailByDate,
  getStrengthDetailByExercise,
  getStrengthExerciseSummary,
  getStrengthProgramSummary,
  getStrengthTotalWeightLifted,
  getStrengthTotalWeightLiftedAgainstGoalsPerSession,
  getStrengthTotalWeightLiftedAsPDF,
  getStrengthWorkoutComparison,
  getTCAMembers,
  updateMember,
  updateMemberStatus,
};
